import { stages } from '@innovamat/radiance-utils';
import { useCheckIsUpdatePeriod } from '../../components/update-period-announcement-panel';
import {
  canAccessReports,
  canClassroomTeacherLeaveClassroom,
  canDeleteClassroom,
  canTeacherLeaveClassroom,
  CLASSROOM_TYPE,
  isJourneyClassroom,
  type Classroom,
} from '../modules/classroom';

import type { ClassroomTeacher } from '../modules/teacher';
import { useOrganization } from './use-organization';
import { usePermissionCourses } from './use-permission-courses';

type ClassroomStudentsCapabilities = {
  canBeAdded: boolean;
  canBeManaged: boolean;
  canDemoBeAdded: boolean;
};

type CurrentClassroomCapabilities = {
  canDelete: boolean;
  canTeacherLeave: (teacherId: string) => boolean;
  canClassroomTeacherLeave: (teacher: ClassroomTeacher) => boolean;
  canBeSynchronizedWithGoogle: boolean;
  canHaveFluencyReports: boolean;
  canEditAlias: boolean;
  shouldShowAlias: boolean;
};

type ClassroomPracticeCapabilities = {
  canLevelBeChanged: boolean;
};

type ClassroomContentsCapabilities = {
  canNavigateByGrade: boolean;
};

type ClassroomReportsCapabilities = {
  canAccessReports: boolean;
};

type ClassroomCapabilities = {
  students: ClassroomStudentsCapabilities;
  classroom: CurrentClassroomCapabilities;
  practice: ClassroomPracticeCapabilities;
  contents: ClassroomContentsCapabilities;
  reports: ClassroomReportsCapabilities;
};

type Props = {
  classroom?: Classroom | null;
};

function useClassroomCapabilities({ classroom }: Props): ClassroomCapabilities {
  const isUpdatePeriod = useCheckIsUpdatePeriod();
  const { isRosteringEnabled, isJourneysEnabled } = useOrganization();
  const isPrimaryCourse = stages.isPrimaria(classroom?.courseOrder);
  const isInfantilCourse = stages.isInfantil(classroom?.courseOrder);
  const {
    permissionsByCurrentCourse: { platformEnabled: isPlatformEnabled },
  } = usePermissionCourses({
    courseId: classroom?.courseId,
  });

  return {
    classroom: {
      canDelete: canDeleteClassroom({
        classroom,
        isUpdatePeriod,
      }),
      canTeacherLeave: (teacherId: string) =>
        canTeacherLeaveClassroom({
          classroom,
          teacherId,
        }),
      canClassroomTeacherLeave: (teacher: ClassroomTeacher) =>
        canClassroomTeacherLeaveClassroom({
          teacher,
        }),
      canBeSynchronizedWithGoogle: !isRosteringEnabled,
      canHaveFluencyReports:
        Boolean(isJourneyClassroom(classroom as Classroom)) ||
        Boolean(isPrimaryCourse),
      canEditAlias: Boolean(
        classroom?.type === CLASSROOM_TYPE.CLASSROOM && isRosteringEnabled
      ),
      shouldShowAlias: Boolean(isRosteringEnabled && classroom?.alias),
    },
    contents: {
      canNavigateByGrade: !isJourneyClassroom(classroom as Classroom),
    },
    practice: {
      canLevelBeChanged: !isJourneyClassroom(classroom as Classroom),
    },
    reports: {
      canAccessReports: canAccessReports(
        classroom as Classroom,
        isJourneysEnabled
      ),
    },
    students: {
      canBeAdded: !isUpdatePeriod && isPlatformEnabled && !isRosteringEnabled,
      canBeManaged:
        isPlatformEnabled || isJourneyClassroom(classroom as Classroom),
      canDemoBeAdded:
        isPlatformEnabled &&
        !isUpdatePeriod &&
        !isJourneysEnabled &&
        (!!isInfantilCourse || !!isPrimaryCourse),
    },
  };
}

export { useClassroomCapabilities };
export type {
  ClassroomCapabilities,
  ClassroomReportsCapabilities,
  ClassroomStudentsCapabilities,
};
